import Box from "@memorang/ui/components/Box";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import type { ActiveCodesResponse } from "api/commerce/types";
import ProductIcon from "components/ProductIcon";
import { useCallback, useState } from "react";
import { Avatar, Button, Card, Divider, List, Text } from "react-native-paper";
import { useRedeemCopilot } from "../context";

export type PurchasesProps = {
	items: ActiveCodesResponse[];
};

export const Purchases = ({ items }: PurchasesProps) => {
	const [expandedId, setExpandedId] = useState<string | null>(
		items?.length > 0 ? `purchase-${items[0]?.bundle?.id}` : null,
	);
	const [clickedIds, setClickedIds] = useState<string[]>([]);
	const { pushContextMessage } = useRedeemCopilot();

	const theme = useAppTheme();

	const handleAccordionToggle = useCallback((id: string) => {
		setExpandedId((prevId) => (prevId === id ? null : id));
	}, []);

	const renderIcon = (bundle: ActiveCodesResponse["bundle"]) => {
		if (bundle?.icon?.url) {
			return <ProductIcon image={bundle.icon.url} size={48} />;
		}

		return (
			<Avatar.Text
				size={48}
				label={(bundle?.name?.substring(0, 2) || "??").toUpperCase()}
			/>
		);
	};

	if (!items || items.length === 0) {
		return;
	}

	return (
		<Card mode="elevated" style={{ overflow: "hidden" }}>
			<Card.Title
				title="🎉 Found Access Codes"
				titleVariant="titleLarge"
				subtitle="Redeem to get access to your content"
			/>
			<Divider />
			<List.AccordionGroup>
				{items?.map((item) => {
					const bundle = item.bundle;
					const firstCode = item.codes?.[0] || "";
					const numCodes = item.codes?.length || 0;
					const accordionId = `purchase-${bundle.id}`;
					const isExpanded = expandedId === accordionId;

					return (
						<List.Accordion
							key={accordionId}
							id={accordionId}
							title={<Text variant="titleMedium">{bundle.name}</Text>}
							titleNumberOfLines={2}
							expanded={isExpanded}
							onPress={() => handleAccordionToggle(accordionId)}
							left={(props) => (
								<List.Icon icon={() => renderIcon(bundle)} {...props} />
							)}
							right={(props) => (
								<Button
									{...props}
									mode="outlined"
									onPress={() => {
										setClickedIds((prev) => [...prev, bundle.id]);
										pushContextMessage(`Redeem ${firstCode}`);
									}}
									disabled={clickedIds.includes(bundle.id)}
								>
									<Text variant="labelSmall">Redeem</Text>
								</Button>
							)}
							style={{
								backgroundColor: theme.colors.elevation.level1,
							}}
						>
							<Box
								style={{
									paddingHorizontal: 16,
									paddingTop: 16,
									paddingBottom: 32,
									backgroundColor: theme.colors.elevation.level5,
								}}
							>
								{numCodes > 0 && (
									<Box style={{ marginBottom: 8 }}>
										<Text
											variant="labelSmall"
											style={{ opacity: 0.7, marginBottom: 4 }}
										>
											Access Code{numCodes > 1 ? "s" : ""}
										</Text>
										<Text variant="bodyMedium">
											{firstCode}
											{numCodes > 1 && ` (+ ${numCodes - 1} more)`}
										</Text>
									</Box>
								)}

								{bundle.features && bundle.features.length > 0 && (
									<Box>
										<Text
											variant="labelSmall"
											style={{ opacity: 0.7, marginBottom: 4 }}
										>
											Features
										</Text>
										{bundle.features.map((feature) => (
											<Text
												key={`feature-${bundle.id}-${feature.replace(/\s+/g, "-").toLowerCase()}`}
												variant="bodySmall"
												style={{ marginBottom: 2 }}
											>
												• {feature}
											</Text>
										))}
									</Box>
								)}
							</Box>
						</List.Accordion>
					);
				})}
			</List.AccordionGroup>
		</Card>
	);
};
